// WorkoutLogModal — the confirm/review modal for Mirror-driven workout capture.
// Voice flow: propose_workout_log -> workout-extract -> prefill (source 'voice').
//
// Mirrors the FocusLogModal mold: reuses the exported AgendaBottomSheet for
// premium styling, hydrates editable fields from a prefill, writes via
// useWorkout.saveWorkoutSession (source + client_action_id idempotency), then
// emits a capture recap.
//
// Perf: useWorkout exposes only the writer (no mount fetch), so it's cheap even
// though useModuleCapture keeps this in the tree; the outer component still
// returns null until `open`.
//
// Duration is OPTIONAL here (unlike Focus): a named workout with no time logged
// is valid — an empty / 0 duration saves as null.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useWorkout } from "@/hooks/useWorkout";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { emitCaptureRecap } from "@/lib/capture/captureRecapBus";
import { sfx } from "@/lib/sfx";

export interface WorkoutPrefill {
  name: string;
  /** Minutes, or null when the user gave no duration. */
  duration_minutes: number | null;
  /** 'voice' for spoken capture. */
  source: "voice";
  /** Per-turn idempotency key — "mirror_msg_<action_id>". */
  client_action_id?: string;
  /** The original capture owner; authentication cannot substitute another user. */
  expected_user_id?: string;
  /** Optional raw phrase for the "you said" reference. */
  transcriptRaw?: string | null;
}

interface WorkoutLogModalProps {
  open: boolean;
  prefill: WorkoutPrefill | null;
  onClose: () => void;
  onCreated: () => void;
  isCurrent?: () => boolean;
}

export default function WorkoutLogModal({ open, prefill, onClose, onCreated, isCurrent }: WorkoutLogModalProps) {
  if (!open || !prefill || isCurrent?.() === false) return null;
  return <WorkoutLogModalInner prefill={prefill} onClose={onClose} onCreated={onCreated} isCurrent={isCurrent} />;
}

function WorkoutLogModalInner({
  prefill,
  onClose,
  onCreated,
  isCurrent,
}: {
  prefill: WorkoutPrefill;
  onClose: () => void;
  onCreated: () => void;
  isCurrent?: () => boolean;
}) {
  const { t } = useLanguage();
  const { saveWorkoutSession } = useWorkout();
  const mounted = useRef(false), saveLock = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = () => mounted.current && (isCurrent?.() ?? true);

  const [name, setName] = useState(prefill.name ?? "");
  const [duration, setDuration] = useState(prefill.duration_minutes ?? 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saveLock.current || !current()) return;
    if (!name.trim()) {
      toast.error(t("workout.saveError"));
      return;
    }
    saveLock.current = true; setSaving(true);
    try {
      const saved = await saveWorkoutSession(name.trim(), duration > 0 ? duration : null, {
        source: prefill.source,
        clientActionId: prefill.client_action_id,
        ownerId: prefill.expected_user_id,
        isCurrent: current,
      });
      if (!current()) return;
      if (!saved) {
        sfx.error("module");
        toast.error(t("workout.saveError"));
        return;
      }
      sfx.confirm("module");
      emitCaptureRecap({
        kind: "workout",
        title: t("mirror.recap.workout"),
        subtitle: duration > 0 ? name.trim() + " · " + duration + " min" : name.trim(),
      });
      onCreated();
      onClose();
    } catch (e) {
      if (!current()) return;
      console.error("[WorkoutLogModal] save failed:", e);
      sfx.error("module");
      toast.error(t("workout.saveError"));
    } finally {
      saveLock.current = false; if (current()) setSaving(false);
    }
  };

  const inputStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(187 72% 50% / 0.15)",
  };
  const labelStyle = { color: "hsl(187 72% 50% / 0.55)" };

  const footer = (
    <button
      onClick={handleSave}
      disabled={saving}
      className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
      style={{
        background: saving ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50%)",
        color: "hsl(var(--background))",
      }}
    >
      {saving ? t("agenda.saving") : t("workout.log.save")}
    </button>
  );

  return (
    <AgendaBottomSheet open onClose={onClose} title={t("workout.log.title")} footer={footer}>
      {prefill.transcriptRaw && (
        <div
          className="mb-4 rounded-xl px-3 py-2.5"
          style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.18)" }}
        >
          <span
            className="text-[9px] font-display tracking-[0.18em] uppercase mb-1 block"
            style={{ color: "hsl(187 72% 60% / 0.75)" }}
          >
            {t("agenda.voice.youSaid")}
          </span>
          <p className="text-[12px] font-display text-muted-foreground italic leading-snug">
            &ldquo;{prefill.transcriptRaw}&rdquo;
          </p>
        </div>
      )}

      {/* Name */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("workout.log.name")}
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display text-foreground placeholder:text-muted-foreground outline-none"
          style={inputStyle}
        />
      </label>

      {/* Duration (optional) */}
      <label className="block mb-2">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("workout.log.duration")}
        </span>
        <input
          type="number"
          value={duration}
          onChange={(e) => setDuration(Math.max(0, Number(e.target.value) || 0))}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display font-semibold text-foreground outline-none"
          style={inputStyle}
        />
      </label>
    </AgendaBottomSheet>
  );
}
